import React, {useState, useEffect} from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Switch from '@material-ui/core/Switch';
import Badge from '@material-ui/core/Badge';
import {useDataProvider} from 'react-admin';
import {Typography as UITypography} from '../../UIComponents/Wrappers';
import moment from 'moment';

const RefreshSelectedDialog = ({open, onClose, onRefreshSelected, payrollData}) => {
    const dataProvider = useDataProvider();
    const [employees, setEmployees] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectAll, setSelectAll] = useState(false);
    const [filters, setFilters] = useState({
        site: null,
        costCenter: null,
        employmentType: null,
        region: null,
        newOnly: false,
        employeeName: '',
    });
    const [sites, setSites] = useState([]);
    const [costCenters, setCostCenters] = useState([]);
    const [regions, setRegions] = useState([]);

    const employmentTypes = [
        {id: 'FullTime', name: 'FullTime'},
        {id: 'Contract', name: 'Contract'},
        {id: 'PartTime', name: 'PartTime'},
        {id: 'InternOrFellow', name: 'InternOrFellow'},
        {id: 'Casual', name: 'Casual'},
    ];

    useEffect(() => {
        if (open) {
            loadEmployees();
            loadFilterOptions();
            setSelectedIds([]);
            setSelectAll(false);
            setFilters({site: null, costCenter: null, employmentType: null, region: null, newOnly: false, employeeName: ''});
        }
    }, [open]);

    const loadFilterOptions = async () => {
        try {
            const [sitesRes, costCentersRes, regionsRes] = await Promise.all([
                dataProvider.getList('sites', {filter: {}, pagination: {}, sort: {}}),
                dataProvider.getList('costCenters', {filter: {}, pagination: {}, sort: {}}),
                dataProvider.getList('regions', {filter: {}, pagination: {}, sort: {}}),
            ]);
            setSites(sitesRes.data || []);
            setCostCenters(costCentersRes.data || []);
            setRegions(regionsRes.data || []);
        } catch (e) {
            console.error('Failed to load filter options:', e);
        }
    };

    const loadEmployees = async () => {
        setLoading(true);
        try {
            const {data} = await dataProvider.getList('employees', {
                pagination: {page: 1, perPage: 10000},
                sort: {field: 'name', order: 'ASC'},
                filter: {filter: 'active eq true', select: 'id,name,fatherName,gFatherName,siteId,costCenterId,employmentType,basicSalary,startDate'},
            });
            setEmployees(data || []);
        } catch (e) {
            console.error('Failed to load employees:', e);
        } finally {
            setLoading(false);
        }
    };

    const getFullName = (employee) => {
        return `${employee.name} ${employee.fatherName || ''} ${employee.gFatherName || ''}`.trim();
    };

    const existingEmployeeIds = new Set(payrollData.map(p => p.employeeId));

    const employeesWithNewFlag = employees.map(emp => ({
        ...emp,
        isNew: !existingEmployeeIds.has(emp.id),
    }));

    const filteredEmployees = employeesWithNewFlag.filter(emp => {
        if (filters.site && emp.siteId !== filters.site) return false;
        if (filters.employmentType && emp.employmentType !== filters.employmentType) return false;
        if (filters.costCenter && emp.costCenterId !== filters.costCenter) return false;
        if (filters.region) {
            const site = sites.find(s => s.id === emp.siteId);
            if (!site || site.regionId !== filters.region) return false;
        }
        if (filters.newOnly && !emp.isNew) return false;
        if (filters.employeeName && !getFullName(emp).toLowerCase().includes(filters.employeeName.toLowerCase())) return false;
        return true;
    });

    const displayedEmployees = filteredEmployees;

    const handleToggleEmployee = (employeeId) => {
        setSelectedIds(prev =>
            prev.includes(employeeId)
                ? prev.filter(id => id !== employeeId)
                : [...prev, employeeId]
        );
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedIds([]);
        } else {
            setSelectedIds(displayedEmployees.map(emp => emp.id));
        }
        setSelectAll(!selectAll);
    };

    useEffect(() => {
        if (displayedEmployees.length > 0 && selectedIds.length === displayedEmployees.length) {
            setSelectAll(true);
        } else if (selectedIds.length > 0) {
            setSelectAll(false);
        } else {
            setSelectAll(false);
        }
    }, [selectedIds, displayedEmployees.length]);

    const handleRefresh = () => {
        if (selectedIds.length > 0) {
            onRefreshSelected(selectedIds);
            onClose();
        }
    };

    const siteNameLookup = {};
    sites.forEach(s => {
        siteNameLookup[s.id] = s.name;
    });

    const regionNameLookup = {};
    regions.forEach(r => {
        regionNameLookup[r.id] = r.name;
    });

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Refresh Selected Employees</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} style={{marginBottom: 16}}>
                    <Grid item xs={2}>
                        <TextField
                            fullWidth
                            margin="dense"
                            label="Employee Name"
                            value={filters.employeeName}
                            onChange={e => setFilters({...filters, employeeName: e.target.value})}
                            placeholder="Search by name..."
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <FormControl fullWidth margin="dense">
                            <InputLabel>Site</InputLabel>
                            <Select
                                value={filters.site || ''}
                                onChange={e => setFilters({...filters, site: e.target.value || null})}
                            >
                                <MenuItem value="">All Sites</MenuItem>
                                {sites.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={2}>
                        <FormControl fullWidth margin="dense">
                            <InputLabel>Region</InputLabel>
                            <Select
                                value={filters.region || ''}
                                onChange={e => setFilters({...filters, region: e.target.value || null})}
                            >
                                <MenuItem value="">All Regions</MenuItem>
                                {regions.map(r => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                        <FormControl fullWidth margin="dense">
                            <InputLabel>Employment Type</InputLabel>
                            <Select
                                value={filters.employmentType || ''}
                                onChange={e => setFilters({...filters, employmentType: e.target.value || null})}
                            >
                                <MenuItem value="">All Types</MenuItem>
                                {employmentTypes.map(et => <MenuItem key={et.id} value={et.id}>{et.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                        <FormControl fullWidth margin="dense">
                            <InputLabel>Cost Center</InputLabel>
                            <Select
                                value={filters.costCenter || ''}
                                onChange={e => setFilters({...filters, costCenter: e.target.value || null})}
                            >
                                <MenuItem value="">All Cost Centers</MenuItem>
                                {costCenters.map(cc => <MenuItem key={cc.id} value={cc.id}>{cc.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                <div style={{marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 8px'}}>
                    <FormControlLabel
                        control={<Checkbox checked={selectAll} onChange={handleSelectAll} color="primary"/>}
                        label={<UITypography weight="bold">Select All ({displayedEmployees.length})</UITypography>}
                    />
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <FormControlLabel
                            control={<Switch checked={filters.newOnly} onChange={e => setFilters({...filters, newOnly: e.target.checked})}/>}
                            label="New Only"
                        />
                        <Typography variant="body2" color="textSecondary" style={{marginLeft: 8}}>
                            {selectedIds.length} selected
                        </Typography>
                    </div>
                </div>

                <div style={{maxHeight: 400, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 4}}>
                    {loading ? (
                        <div style={{display: 'flex', justifyContent: 'center', padding: 32}}>
                            <CircularProgress/>
                        </div>
                    ) : displayedEmployees.length === 0 ? (
                        <Typography variant="body2" style={{padding: 16, textAlign: 'center', color: '#999'}}>
                            No employees match the current filters
                        </Typography>
                    ) : (
                        <table style={{width: '100%', borderCollapse: 'collapse'}}>
                            <thead style={{position: 'sticky', top: 0, backgroundColor: '#fafafa', zIndex: 1}}>
                                <tr>
                                    <th style={{width: 40, padding: 8, borderBottom: '2px solid #e0e0e0', textAlign: 'center'}}></th>
                                    <th style={{padding: 8, borderBottom: '2px solid #e0e0e0', textAlign: 'left', fontWeight: 'bold', fontSize: 13}}>Name</th>
                                    <th style={{padding: 8, borderBottom: '2px solid #e0e0e0', textAlign: 'left', fontWeight: 'bold', fontSize: 13}}>Start Date</th>
                                    <th style={{padding: 8, borderBottom: '2px solid #e0e0e0', textAlign: 'right', fontWeight: 'bold', fontSize: 13}}>Basic Salary</th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayedEmployees.map(emp => (
                                    <tr
                                        key={emp.id}
                                        style={{
                                            backgroundColor: selectedIds.includes(emp.id) ? '#f5f5f5' : 'transparent',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => handleToggleEmployee(emp.id)}
                                    >
                                        <td style={{padding: 8, borderBottom: '1px solid #f0f0f0', textAlign: 'center'}}>
                                            <Checkbox
                                                checked={selectedIds.includes(emp.id)}
                                                onChange={() => handleToggleEmployee(emp.id)}
                                                onClick={e => e.stopPropagation()}
                                                color="primary"
                                            />
                                        </td>
                                        <td style={{padding: 8, borderBottom: '1px solid #f0f0f0'}}>
                                            <Badge
                                                badgeContent={emp.isNew ? 'New' : null}
                                                color="primary"
                                            >
                                                <Typography variant="body2">
                                                    {getFullName(emp)}
                                                    <span style={{color: '#999', marginLeft: 8, fontSize: 12}}>
                                                        ({emp.employmentType})
                                                        {emp.siteId && ` - ${siteNameLookup[emp.siteId] || ''}`}
                                                    </span>
                                                </Typography>
                                            </Badge>
                                        </td>
                                        <td style={{padding: 8, borderBottom: '1px solid #f0f0f0'}}>
                                            <Typography variant="body2" style={{color: '#666'}}>
                                                {emp.startDate ? moment(emp.startDate).format('DD/MM/YYYY') : '-'}
                                            </Typography>
                                        </td>
                                        <td style={{padding: 8, borderBottom: '1px solid #f0f0f0', textAlign: 'right'}}>
                                            <Typography variant="body2" style={{fontFamily: 'monospace'}}>
                                                {emp.basicSalary ? emp.basicSalary.toLocaleString() : '-'}
                                            </Typography>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="default">
                    Cancel
                </Button>
                <Button
                    onClick={handleRefresh}
                    color="primary"
                    variant="contained"
                    disabled={selectedIds.length === 0 || loading}
                >
                    Refresh Selected ({selectedIds.length})
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RefreshSelectedDialog;
